import {
  type CategoryOption,
  categoryOptionsFromPlantProfiles,
} from "portal/utils/categoryCollection/category";
import { DEBOUNCE_MS } from "portal/utils/timing";
import {
  FeatureFlag,
  useFeatureFlagForCustomer,
} from "portal/utils/hooks/useFeatureFlag";
import { portalEndpoints } from "portal/state/portalApi";
import { useCallback, useMemo, useState } from "react";
import { useDebounce } from "@uidotdev/usehooks";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 25;

type UsePlantProfileCategoryOptionsConfig = {
  /** Refetch when the hook mounts instead of serving cached list data. */
  refetchOnMount?: boolean;
};

export const usePlantProfileCategoryOptions = (
  customerUuid?: string,
  { refetchOnMount = false }: UsePlantProfileCategoryOptionsConfig = {}
): {
  categoryOptions: CategoryOption[];
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  onSearchInputChange: (text: string) => void;
} => {
  const { t, i18n } = useTranslation();
  const [searchInput, setSearchInput] = useState("");
  const searchText = useDebounce(searchInput.trim(), DEBOUNCE_MS);
  const {
    isEnabled: includeCustomCategories,
    isLoading: isFeatureFlagLoading,
  } = useFeatureFlagForCustomer(FeatureFlag.CUSTOM_CATEGORIES, customerUuid);

  const effectiveSearch = searchText || undefined;

  const {
    data,
    hasNextPage: hasMore,
    fetchNextPage,
    isFetching,
    isLoading,
  } = portalEndpoints.listCustomerPlantProfilesCursor.useInfiniteQuery(
    {
      customerUuid: customerUuid ?? "",
      carbon: true,
      limit: PAGE_SIZE,
      search: effectiveSearch,
      includeCustomCategories: includeCustomCategories ?? false,
    },
    {
      skip: !customerUuid || isFeatureFlagLoading,
      refetchOnMountOrArgChange: refetchOnMount,
    }
  );

  const categoryOptions = useMemo(() => {
    const profiles = [];
    for (const page of data?.pages ?? []) {
      for (const profile of page.plantProfiles) {
        profiles.push(profile);
      }
    }
    return categoryOptionsFromPlantProfiles(t, i18n, profiles);
  }, [data, i18n, t]);

  const loadMore = useCallback(() => {
    if (hasMore && !isFetching) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasMore, isFetching]);

  const onSearchInputChange = setSearchInput;

  return {
    categoryOptions,
    isLoading: isFeatureFlagLoading || isLoading,
    hasMore,
    loadMore,
    onSearchInputChange,
  };
};
